import React from "react";
import { Clock, PackageCheck, AlarmClock } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { useServerTick, serverNow, mmss } from "../lib/serverClock";

const COUNTING = ["accepted", "preparing"];

/** Prominent, server-authoritative pickup countdown for the customer order screen. */
export function OrderCountdown({ order }) {
  const { t } = useI18n();
  const status = order?.status;
  const deadline = order?.pickup_deadline ? new Date(order.pickup_deadline).getTime() : null;
  const counting = COUNTING.includes(status) && !!deadline;
  const now = useServerTick(counting);

  if (status === "ready") {
    return (
      <div data-testid="countdown-ready" className="bg-brand-green text-white rounded-3xl p-5 text-center shadow-[0_10px_30px_rgb(0,0,0,0.08)]">
        <PackageCheck size={26} className="mx-auto mb-2" />
        <p className="font-display font-black text-xl leading-tight">
          {t("cd.ready", "VAŠA PORUDŽBINA JE SPREMNA ZA PREUZIMANJE")}
        </p>
      </div>
    );
  }

  if (!counting) return null;

  const diff = Math.round((deadline - (now || serverNow())) / 1000);
  const late = diff < 0;
  const mins = diff / 60;
  const tone = late
    ? { box: "bg-brand-red text-white border-brand-red", sub: "text-white/80" }
    : mins <= 5
      ? { box: "bg-brand-orange text-white border-brand-orange", sub: "text-white/80" }
      : mins <= 10
        ? { box: "bg-white text-brand-orange border-brand-orange", sub: "text-slate-500" }
        : { box: "bg-white text-slate-900 border-slate-100", sub: "text-slate-500" };

  return (
    <div data-testid="countdown-card" role="status" aria-live="off"
      className={`rounded-3xl p-5 text-center border-2 shadow-[0_10px_30px_rgb(0,0,0,0.06)] transition-colors ${tone.box}`}>
      <div className="flex items-center justify-center gap-2">
        {late ? <AlarmClock size={16} /> : <Clock size={16} />}
        <span className="text-[11px] font-black uppercase tracking-[0.2em]">
          {late ? t("cd.late", "KASNI") : t("cd.remaining", "PREOSTALO")}
        </span>
      </div>
      <p data-testid="countdown-value" className={`font-display font-black tracking-tight ${late ? "text-4xl" : "text-5xl"} mt-1`}>
        {late ? `+${mmss(-diff)}` : mmss(diff)}
      </p>
      <p className={`text-sm mt-1 font-semibold ${tone.sub}`} data-testid="countdown-pickup-time">
        {t("cd.pickup_time", "Vreme preuzimanja")} · {order.confirmed_pickup_time || order.pickup_time}
      </p>
      {late && (
        <p className="text-xs mt-2 text-white/90" data-testid="countdown-late-note">
          {t("cd.waiting_ready", "Čeka se potvrda da je porudžbina spremna")}
        </p>
      )}
      {!late && diff <= 0 && (
        <p className="text-xs mt-2" data-testid="countdown-reached">{t("cd.reached", "Vreme preuzimanja je stiglo")}</p>
      )}
    </div>
  );
}

/** Compact remaining-time label for active order cards (no seconds). */
export function CompactRemaining({ order, now }) {
  const { t } = useI18n();
  if (order.status === "ready") return <span data-testid={`compact-time-${order.order_number}`} className="text-xs font-bold text-brand-green">{t("cd.compact_ready", "Spremno")}</span>;
  if (!COUNTING.includes(order.status) || !order.pickup_deadline) return null;
  const diff = Math.round((new Date(order.pickup_deadline).getTime() - now) / 1000);
  let label;
  if (diff < 0) {
    const lateMin = Math.max(1, Math.floor(-diff / 60));
    label = t("cd.compact_late", "Kasni {n} min").replace("{n}", lateMin);
  } else if (diff < 60) {
    label = t("cd.compact_lt1", "<1 min");
  } else {
    label = `${Math.floor(diff / 60)} min`;
  }
  return <span data-testid={`compact-time-${order.order_number}`} className={`text-xs font-bold ${diff < 0 ? "text-brand-red" : "text-slate-600"}`}>{label}</span>;
}
